import { formatTimeOfDay, isoAttr } from "../../lib/datetime.ts";
import { type ReactNode } from "react";
import { ScreenScaffold, type ScreenState } from "../ScreenScaffold.tsx";
import { useIdentity, roleAtLeast } from "../../state/identity.tsx";
import { useTimeRange } from "../../state/time-range.tsx";
import { LivePauseControl } from "../../chrome/LivePauseControl.tsx";
import { TimeRangeControl } from "../../chrome/TimeRangeControl.tsx";
import { useTraces, type TracePage, type TraceSummary, type TracesWindow } from "./use-traces.ts";
import { isTracesEmpty } from "./vocab.ts";
import { TraceList } from "./TraceList.tsx";
import {
  DashboardGrid,
  DashboardGridFull,
  KpiCard,
  ChartCard,
  type KpiCardProps,
} from "../../components/observability/index.ts";
import type { ChartData, ChartSeries } from "../../components/Chart.tsx";
import "./traces.css";

/**
 * The Traces screen (task 11.11; design 12.2 Traces row). It lists the traces in the current window
 * from the REST read `GET /api/v1/traces` (role `viewer`) and drills each trace to its cross-service
 * waterfall at `/traces/{id}` (the detail screen, {@link TraceDetailScreen}).
 *
 * THE INDEPENDENT inferred/partial RULE: every trace row shows its `inferred` and `partial` labels
 * as two SEPARATE, independent badges — both when both apply, neither taking precedence — so an
 * operator sees all four combinations (none / inferred / partial / both) across the list at once.
 *
 * The list read is WINDOWED (it takes `from`/`to`), so the window is resolved from the shared
 * time-range and pausing freezes the query exactly as it freezes the other windowed screens
 * (design 12.2, FR-13 AC2).
 *
 * It handles every state a screen must (task 11 preamble; design 12.3):
 * - restricted: the read is viewer-level; an unknown/below-viewer role sees the restricted state.
 * - loading / error / empty / ready as the query resolves; a cold-core waking error defers to the
 *   shell's banner (kept "loading" under it so there is no contradiction).
 *
 * Observability-ui Stage 5 (task 5.1; design §Investigate): the ready body now composes the
 * observability kit over the SAME `GET /api/v1/traces` page it has always read — this stage is
 * PRESENTATION-ONLY, so no data layer changes. A `DashboardGrid` lays out a `KpiCard` row derived
 * from the page's own trace rows (total, errored, denied, services touched), a `ChartCard` of the
 * trace volume per time bucket (a real time dimension — `started_at` bucketed across the window;
 * honest empty until there are two buckets, never a fabricated line, design Property 2), and the
 * existing semantic `TraceList` kept as the detailed table surface inside a kit panel. Every surface
 * derives its colours from the theme tokens via the kit's CSS, so the screen reads correctly in
 * both light and dark (design Property 1).
 */
export function TracesScreen(): ReactNode {
  const { role } = useIdentity();
  const { live } = useTimeRange();
  const { query, window } = useTraces();

  const state = resolveState(role, query);

  return (
    <ScreenScaffold
      title="Traces"
      subtitle="Trace list across services. Each trace's inferred and partial labels are independent and may both show. Open a trace for its cross-service waterfall."
      state={state}
    >
      {query.data !== undefined && (
        <TracesBody page={query.data} window={window} live={live} />
      )}
    </ScreenScaffold>
  );
}

/** Decide which scaffold state to show from the role and the query status. */
function resolveState(
  role: ReturnType<typeof useIdentity>["role"],
  query: ReturnType<typeof useTraces>["query"],
): ScreenState {
  // Least privilege: an unknown or below-viewer role sees the restricted state, not an error.
  if (!roleAtLeast(role, "viewer")) {
    return {
      kind: "restricted",
      reason:
        role === "unknown"
          ? "Your role has not been resolved yet, so the traces view is hidden until it is. The server enforces access regardless."
          : "The traces view needs at least the viewer role.",
    };
  }

  if (query.data !== undefined) {
    if (isTracesEmpty(query.data)) {
      return {
        kind: "empty",
        cause:
          "No traces were recorded in this window. Widen the time range, or check that the services are reporting spans.",
      };
    }
    return { kind: "ready" };
  }

  if (query.isError) {
    // The cold-core waking error is the shell's banner; keep "loading" under it (no contradiction).
    if (query.error.isWaking) return { kind: "loading" };
    return { kind: "error", message: errorMessage(query.error) };
  }

  return { kind: "loading" };
}

function errorMessage(error: ReturnType<typeof useTraces>["query"]["error"]): string {
  if (error === null) return "The traces view could not load.";
  switch (error.kind) {
    case "unauthorized":
      return "Your session has expired. Reload to sign in again.";
    case "forbidden":
      return "You are not permitted to see traces. The server enforces access.";
    case "network":
      return "The traces request could not be sent. Check your connection.";
    default:
      return error.message;
  }
}

function TracesBody(props: {
  page: TracePage;
  window: TracesWindow;
  live: boolean;
}): ReactNode {
  const { page, window } = props;
  const generatedAt = page.meta.generated_at;
  const sampling = page.meta.data_quality.sampling_in_effect;

  const kpis = traceKpiCards(page.items);
  const volume = traceVolumeChart(page.items, window);

  return (
    <div className="trc" data-live={props.live}>
      <div className="screen-meta-row">
        <LivePauseControl />
        <TimeRangeControl />
        <p className="trc__meta">
        {props.live ? "Live" : "Paused"} · window{" "}
        <time dateTime={isoAttr(window.fromMs)}>
          {formatTimeOfDay(window.fromMs)}
        </time>{" "}
        to{" "}
        <time dateTime={isoAttr(window.toMs)}>
          {formatTimeOfDay(window.toMs)}
        </time>{" "}
        · generated{" "}
        <time dateTime={isoAttr(generatedAt)}>
          {formatTimeOfDay(generatedAt)}
        </time>
        {sampling && (
          <span className="trc__meta-flag" role="note">
            {" "}
            · sampling in effect. Some traces may be partial
          </span>
        )}
      </p>
      </div>

      <DashboardGrid
        label="Traces dashboard"
        kpis={kpis.map((kpi) => (
          <KpiCard key={kpi.key} {...kpi.props} />
        ))}
      >
        {/* Trace volume over time, bucketed from the page's own `started_at` values. The ChartCard
            shows the Chart wrapper's honest empty state until there are at least two buckets — no
            fabricated line (design Property 2). */}
        <ChartCard
          title={volume.title}
          unit={volume.unit}
          data={volume.data}
          series={volume.series}
          {...(volume.empty !== undefined ? { empty: volume.empty } : {})}
        />

        {/* The existing semantic trace list, kept as the detailed table surface inside a kit panel
            (design §Investigate: StatusGrid/table + ChartCards). It still owns the drill to each
            trace's waterfall and the independent inferred/partial badges. */}
        <DashboardGridFull>
          <section className="obs-panel trc-list-card" aria-label="Trace list">
            <TraceList traces={page.items} />
          </section>
        </DashboardGridFull>
      </DashboardGrid>
    </div>
  );
}

/** A KpiCard plus a stable React key, so the body can map a list without inventing keys. */
interface KeyedKpi {
  readonly key: string;
  readonly props: KpiCardProps;
}

/**
 * The headline trace counts as KpiCards, derived ONLY from the page the screen already read — no new
 * fetch and nothing fabricated. The counts are facts off `page.items`: how many traces are in the
 * window, how many have a worst status of error or denied, and how many distinct services the window
 * touched. These are exact tallies of what was returned, so no estimate/measure is attached.
 */
function traceKpiCards(items: readonly TraceSummary[]): readonly KeyedKpi[] {
  const total = items.length;
  const errored = items.filter((t) => t.worst_status === "error").length;
  const denied = items.filter((t) => t.worst_status === "denied").length;
  const services = new Set<string>();
  for (const t of items) for (const s of t.services) services.add(s);

  return [
    { key: "total", props: { label: "Traces", value: total } },
    { key: "errored", props: { label: "Errored", value: errored } },
    { key: "denied", props: { label: "Denied", value: denied } },
    { key: "services", props: { label: "Services touched", value: services.size } },
  ];
}

/** A trace-volume chart card plus its stable key and (when there is no data) an honest cause. */
interface KeyedChart {
  readonly title: string;
  readonly unit: string;
  readonly data: ChartData;
  readonly series: readonly ChartSeries[];
  readonly empty?: string;
}

/** The single accent-stroked series the volume chart draws; theme-resolved by the Chart wrapper. */
const TRACE_VOLUME_SERIES: readonly ChartSeries[] = [
  { label: "traces", colorVar: "--accent", width: 1.5 },
];

/**
 * The number of buckets the window is split into for the trace-volume chart. A fixed count keeps the
 * x axis stable as the window slides, so the chart reads as a steady trend rather than jumping its
 * bucket width.
 */
const TRACE_VOLUME_BUCKETS = 24;

/**
 * Build the trace-volume ChartCard data from the page's own rows: split the resolved window into a
 * fixed number of equal time buckets and count how many traces STARTED in each (by `started_at`).
 * This is real data off the existing read — a trace is counted only where it actually began. When
 * fewer than two buckets carry any trace, the card is handed an empty dataset and an honest cause so
 * the Chart wrapper shows its empty state rather than a flat fabricated line (design Property 2).
 */
function traceVolumeChart(
  items: readonly TraceSummary[],
  window: TracesWindow,
): KeyedChart {
  const base = { title: "Trace volume", unit: "per bucket", series: TRACE_VOLUME_SERIES };
  const span = window.toMs - window.fromMs;
  if (span <= 0 || items.length === 0) {
    return { ...base, data: [[], []], empty: "No traces in this window." };
  }

  const width = span / TRACE_VOLUME_BUCKETS;
  const counts = new Array<number>(TRACE_VOLUME_BUCKETS).fill(0);
  let placed = 0;
  for (const t of items) {
    const at = t.started_at ?? NaN;
    if (!Number.isFinite(at) || at < window.fromMs || at > window.toMs) continue;
    const idx = Math.min(
      TRACE_VOLUME_BUCKETS - 1,
      Math.max(0, Math.floor((at - window.fromMs) / width)),
    );
    counts[idx] = (counts[idx] ?? 0) + 1;
    placed += 1;
  }

  // Only draw once at least two buckets carry traces — otherwise there is no trend to show.
  const nonEmpty = counts.filter((c) => c > 0).length;
  if (placed === 0 || nonEmpty < 2) {
    return { ...base, data: [[], []], empty: "Not enough traces in this window to chart a trend." };
  }

  // Bucket start times (Unix ms) on the x axis, so the Chart wrapper's default `time` axis renders
  // them as clock time — the volume is drawn against when traces happened, not an index.
  const xs = counts.map((_, i) => Math.round(window.fromMs + i * width));
  return { ...base, data: [xs, counts] };
}
